'use client';

import { formatDate } from "@repo/ui/lib/utils";
import { InfoField, RiskIndicator, SectionHeader } from "../../arbre/print";
import { formatRiskLabel, formatValueWithSujetLabel, mapRiskToEnglish } from "../../shared";
import { isNotEmpty } from "./components/EpaysageAltPDFDocument";

/**
 * Pure UI component for printing epaysage inventaire data - PRINT-ALT version.
 * Receives ALL data already transformed from the API backend.
 * NO local transformations - just display the data.
 * Matches the exact structure of packages/ui/src/features/inventaires/epaysage/print-alt/_index.tsx
 */

// Type definition matching backend API response from prepareEpaysagePrintData
export interface EpaysagePrintData {
  organization: {
    name?: string;
    name_societe?: string;
    img_logo?: string;
    address_societe?: string;
    ville?: string;
    siret?: string;
  } | null;
  organizationAddress: string;
  organizationCity: string;
  organizationZipCode: string;
  organizationSiret: string;
  organizationNumCertification: string;
  organizationLogo: string | null;
  organizationName: string;
  user?: {
    prenom?: string;
    nom?: string;
  };
  headerTitle: string; // Already formatted by API
  formattedDate: string; // Already formatted by API
  addressLine: string; // Already built by API
  code_site: string;
  num_sujet: string;
  gps: string;
  type_espace: string; // Already resolved label
  surface: string; // Already formatted
  essence: Array<{
    // Transformed essences from API
    id?: number;
    count_subject?: number;
    nbre_sujet_concerne?: number;
    diametre: string; // Already formatted with unit
    hauteur: string; // Already formatted with unit
    stade_dev: string; // Already resolved label
    port_arbre: string; // Already resolved label
    etat_san_general: string; // Already resolved label
    risque: string; // Already resolved label
    critere: string; // Already resolved label
    travaux_aucun?: number | boolean | string;
    travaux_abattage?: number | boolean | string;
    travaux_demontage?: number | boolean | string;
    travaux_mise_en_securite?: number | boolean | string;
    travaux_taille_sanitaire?: number | boolean | string;
    travaux_dechiffrage?: number | boolean | string;
    travaux_couple_selective?: number | boolean | string;
    date_travaux?: string;
    date_pro_visite?: string;
    user_edited_date_travaux?: string;
    user_edited_date_pro_visite?: string;
    travaux_com?: string;
    img1?: string; // Already full URL from API
    img2?: string; // Already full URL from API
    espece?: {
      nom_fr?: string;
      name?: string;
      genre?: string;
    } | null;
  }>;
  essences: string[]; // Array of formatted essence names (max 5)
  essences1?: string;
  essences2?: string;
  essences3?: string;
  essences4?: string;
  essences5?: string;
  totalSubjects: string; // Already formatted
  caracteristiques: {
    milieu: string; // Already resolved label
    sol: string;
    domaine: string;
    proximite: string;
    accessibilite: string;
    taux_freq: string;
    type_passage: string;
    age_estime: string; // Already formatted with "ans"
  };
  environnement: {
    nuisance: string; // Already resolved label
    situation: string;
  };
  risque: {
    general: string; // Already resolved label
  };
  img1: string | null; // Already full URL from API
  img2: string | null;
  img3: string | null;
  comments: string;
}

export interface PrintEpaysageAltProps {
  printData: EpaysagePrintData;
  inventaireId: number;
}

export default function PrintEpaysageAlt({ printData, inventaireId }: PrintEpaysageAltProps) {


  const formatedFields = printData;
  const essences = printData?.essence ?? [];

  const handlePrint = () => {
    window.print();
  };



  return (
    <div className="bg-background min-h-screen">
      {/* Page 1 */}
      <div className="page max-w-5xl mx-auto px-3 py-4 sm:p-4 bg-card">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:gap-6 mb-6">
          {formatedFields.organizationLogo ? (
            <img
              src={formatedFields.organizationLogo}
              alt={formatedFields.organizationName}
              className="w-[200px] max-h-[200px] h-auto shrink-0"
            />
          ) : (
            <div className="w-24 h-24 sm:w-32 sm:h-32 bg-muted flex items-center justify-center border border-border shrink-0">
              <span className="text-xs text-muted-foreground">Logo</span>
            </div>
          )}
          <div className="text-xs min-w-0">
            <p className="font-bold text-sm">{formatedFields.organizationName}</p>
            <p>{formatedFields.organizationAddress}</p>
            <p>{formatedFields.organizationCity}</p>
            <p><span className="font-bold">SIRET :</span> {formatedFields.organizationSiret}</p>
            <p className="mt-2"><span className="font-bold">Recenseur :</span> {formatedFields.user?.prenom?.toUpperCase()} {formatedFields.user?.nom?.toUpperCase()}</p>
            {formatedFields.organizationNumCertification && (
              <p className="mt-2">Certification Dendromap : {formatedFields.organizationNumCertification}</p>
            )}
          </div>
        </div>

        {/* Main Title */}
        <SectionHeader variant="epaysage">FICHE DE SYNTHESE ESPACE BOISE - {inventaireId}</SectionHeader>

        {/* Epaysage Identification */}
        <div className="py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
          <div className="min-w-0">
            <InfoField label="Date" value={formatedFields.formattedDate} />
            <InfoField label="Type" value={formatedFields.type_espace} />
            <InfoField label="Nbr d'Arbres Total" value={formatedFields.totalSubjects} />
          </div>
          <div className="min-w-0">
            <InfoField label="Code" value={formatedFields.code_site} />
            <InfoField label="Identification" value={formatedFields.num_sujet} />
          </div>
          <div className="min-w-0">
            <InfoField label="Essence 1" value={formatedFields.essences[0]} />
            {formatedFields.essences[1] && <InfoField label="Essence 2" value={formatedFields.essences[1]} />}
            {formatedFields.essences[2] && <InfoField label="Essence 3" value={formatedFields.essences[2]} />}
          </div>
          <div className="min-w-0">
            {formatedFields.essences[3] && <InfoField label="Essence 4" value={formatedFields.essences[3]} />}
            {formatedFields.essences[4] && <InfoField label="Essence 5" value={formatedFields.essences[4]} />}
            <InfoField label="Surface Totale" value={formatedFields.surface} />
          </div>
        </div>
        <InfoField label="Adresse" value={formatedFields.addressLine} />

        {/* Caractéristiques */}
        <SectionHeader className="bg-gray-500">Caractéristiques</SectionHeader>
        <div className="py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
          <div className="border-l border-l-2 border-gray-400 pl-2 min-w-0">
            <InfoField label="Milieu" value={formatedFields.caracteristiques.milieu} />
            <InfoField label="Sol" value={formatedFields.caracteristiques.sol} />
            <InfoField label="Nuisance" value={formatedFields.environnement.nuisance} />
          </div>
          <div className="border-l border-l-2 border-gray-400 pl-2 min-w-0">
            <InfoField label="Situation" value={formatedFields.environnement.situation} />
            <InfoField label="Domaine" value={formatedFields.caracteristiques.domaine} />
            <InfoField label="Cohabitation" value={formatedFields.caracteristiques.proximite} />
          </div>
          <div className="border-l border-l-2 border-gray-400 pl-2 min-w-0">
            <InfoField label="Accès" value={formatedFields.caracteristiques.accessibilite} />
            <InfoField label="Fréquentation" value={formatedFields.caracteristiques.taux_freq} />
            <InfoField label="Type de Passage" value={formatedFields.caracteristiques.type_passage} />
          </div>
        </div>
        <div className="py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs items-center">
          <div className="mb-1 p-[2px] flex flex-wrap items-center gap-1">
            <span className="font-bold text-[#4E5155] text-sm sm:text-[15px] print:text-[13px]">Risque global de l'espace boisé : </span>
            <div className="flex items-center gap-2 p-3">
              <span className="text-[14px]">{formatRiskLabel(formatedFields.risque?.general)}</span>
              <RiskIndicator level={mapRiskToEnglish(formatedFields.risque?.general)} />
            </div>
          </div>
          <InfoField label="Age moyen estimé du boisement" value={formatedFields.caracteristiques.age_estime} />
        </div>
        <div className="py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
          {formatedFields.img1 && (
            <img src={formatedFields.img1} alt="Image 1" className="w-full max-w-[20rem] h-auto aspect-[4/3] object-cover rounded border border-border" />
          )}
          {formatedFields.img2 && (
            <img src={formatedFields.img2} alt="Image 2" className="w-full max-w-[20rem] h-auto aspect-[4/3] object-cover rounded border border-border" />
          )}
          {formatedFields.img3 && (
            <img src={formatedFields.img3} alt="Image 3" className="w-full max-w-[20rem] h-auto aspect-[4/3] object-cover rounded border border-border" />
          )}
        </div>
        <div className="py-4 grid grid-cols-1 gap-4 text-xs">
          <InfoField label="Commentaire général de l'Espace Boisé" value={formatedFields.comments} />
        </div>


        {essences.map((essence: any, index: number) => {
          const sectionTitle = `Essence ${index + 1}`;
          const formattedEssenceName = formatedFields.essences[index] || sectionTitle;

          return (
            <EssenceSection
              key={`essence-${index}`}
              essence={essence}
              essenceIndex={index}
              sectionTitle={sectionTitle}
              essenceName={formattedEssenceName}
            />
          );
        })}
      </div>


    </div>
  );
}


function sujetsX(count_subject: string) {
  const count_subject_number = Number.parseInt(count_subject);
  if (Number.isNaN(count_subject_number)) {
    return '';
  }
  return count_subject_number > 0 ? `${count_subject_number > 1 ? 'sujets' : 'sujet'}` : ' ';
}


function EssenceSection({
  essence,
  essenceIndex,
  sectionTitle,
  essenceName
}: {
  essence: any;
  essenceIndex: number;
  sectionTitle: string;
  essenceName: string;
}) {


  return (
    <div className="min-w-0">
      <SectionHeader className="bg-gray-500">{sectionTitle}</SectionHeader>
      <div className="py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
        <div className="border-l border-l-2 border-gray-400 pl-2 min-w-0">
          <InfoField label={sectionTitle} value={essenceName} />
          <InfoField
            label="Nbr d'arbres"
            valueClassName="text-[16px]"
            value={
              essence.count_subject
                ? formatValueWithSujetLabel(essence.count_subject)
                : ''
            }
          />
        </div>
        <div className="border-l border-l-2 border-gray-400 pl-2 min-w-0">
          <InfoField
            label="Diamètre moyen"
            value={essence.diametre}
          />
          <InfoField
            label="Hauteur moyenne"
            value={essence.hauteur}
          />
        </div>
      </div>
      <div className="py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
        {essence.img1 && (
          <img
            src={essence.img1}
            alt={`Image 1 - ${essenceName}`}
            className="w-full max-w-[20rem] h-auto aspect-[4/3] object-cover rounded border border-border"
          />
        )}
        {essence.img2 && (
          <img
            src={essence.img2}
            alt={`Image 2 - ${essenceName}`}
            className="w-full max-w-[20rem] h-auto aspect-[4/3] object-cover rounded border border-border"
          />
        )}
      </div>

      <SectionHeader className="bg-gray-500">DIAGNOSTIC VISUEL</SectionHeader>
      <div className="py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
        <div className="border-l border-l-2 border-gray-400 pl-2 min-w-0">
          <InfoField
            label="Stade de développement"
            value={essence.stade_dev || ' '}
          />
          <InfoField
            label="État physiologique"
            value={essence.etat_san_general || ' '}
          />
        </div>
        <div className="border-l border-l-2 border-gray-400 pl-2 min-w-0">
          <InfoField
            label="Port général"
            value={essence.port_arbre || ' '}
          />
          <InfoField
            label="Critères particuliers"
            value={essence.critere || ' '}
          />
          <InfoField
            label={`Risque de rupture générale essence ${essenceIndex + 1}`}
            className="flex items-center"
            value={
              <div className="flex items-center ml-2">
                <span className="text-[14px] mr-2">{formatRiskLabel(essence.risque)}</span>
                <RiskIndicator level={mapRiskToEnglish(essence.risque)} />
              </div>
            }

          />
        </div>
      </div>

      <div className="font-bold text-gray-900 text-base sm:text-lg">Travaux :</div>
      <div className="py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
        <div className="border-l border-l-2 pl-2 min-w-0">
          {isNotEmpty(essence.travaux_aucun) && (
            <InfoField
              label="- Aucun travaux"
              value={formatValueWithSujetLabel(essence.travaux_aucun)}
            />
          )}
          {isNotEmpty(essence.travaux_abattage) && (
            <InfoField
              label="- Abattage"
              value={formatValueWithSujetLabel(essence.travaux_abattage)}
            />
          )}
          {isNotEmpty(essence.travaux_demontage) && (
            <InfoField
              label="- Démontage"
              value={formatValueWithSujetLabel(essence.travaux_demontage)}
            />
          )}
          {isNotEmpty(essence.travaux_dechiffrage) && (
            <InfoField
              label="- Défrichage"
              value={formatValueWithSujetLabel(essence.travaux_dechiffrage)}
            />
          )}
          {isNotEmpty(essence.travaux_mise_en_securite) && (
            <InfoField
              label="- Mise en sécurité"
              value={formatValueWithSujetLabel(essence.travaux_mise_en_securite)}
            />
          )}
          {isNotEmpty(essence.travaux_taille_sanitaire) && (
            <InfoField
              label="- Taille sanitaire"
              value={formatValueWithSujetLabel(essence.travaux_taille_sanitaire)}
            />
          )}
          {isNotEmpty(essence.travaux_couple_selective) && (
            <InfoField
              label="- Couple sélective"
              value={formatValueWithSujetLabel(essence.travaux_couple_selective)}
            />
          )}
          {isNotEmpty(essence.travaux_other) && (
            <InfoField
              label="- Préservation niche écologique"
              value={formatValueWithSujetLabel(essence.travaux_other)}
            />
          )}
        </div>
        <div className="border-l border-l-2 pl-2 min-w-0">
          <InfoField
            label="Date prévisionnelle des travaux"
            value={essence.user_edited_date_travaux ? `Le ${formatDate(essence.user_edited_date_travaux)}` : ' '}
          />
          <InfoField
            label="Date de la prochaine visite"
            value={essence.user_edited_date_pro_visite ? `Le ${formatDate(essence.user_edited_date_pro_visite)}` : ' '}
          />

          <InfoField
            label="Commentaires"
            value={essence.travaux_com || ' '}
          />
        </div>
      </div>
    </div>
  );
}